import React, { useEffect, useState } from 'react';

import { IEmployer } from '../../../data_models/employer';
import Avatar from '../../common/Avatar';
import { DEFAULT_USER_PROFILE } from '../../../config/defaultAssetUrls';

import { UserEnumTextMapper } from '../../../data_models/enumToTextMapper';
import Card from '../../common/Card';
import useCurrentUser from '../../../hooks/useCurrentUser';
import useAuth from '../../../hooks/useAuth';
import useDisplayNotifications from '../../../hooks/useDisplayNotifications';
import { useFilePicker } from 'use-file-picker';
import { DisplayNotificationType } from '../../../models/DisplayNotification';
import { getEmployerName } from '../../../utils/employerUtils';
import { FlatButton, OutlinedButton } from '../../common/Button';
import { FiEdit3 } from 'react-icons/fi';
import ProfileLabelValue from '../common/ProfileLabelValue';
import { fullAddressString } from '../../../utils/addressUtils';
import { IoIosCheckmarkCircleOutline, IoMdCloseCircleOutline } from 'react-icons/io';
import { EditCompanyInfoLightbox } from '../../lightboxes/profile/EditCompanyInfoEmployer';
import { EditBasicCompanyInfoLightbox } from '../../lightboxes/profile/EditBasicInfoEmployer';
import { Chip } from '../../common/Chip';
import useConfig from '../../../hooks/useConfig';
import { EmployerWallet } from '../../../data_models/employerWallet';
import { EditSocialLinksEmployer } from '../../lightboxes/profile/EditSocialLinksEmployer';
import RichText from '../../common/RichText';



interface EmployerUserProfileProps {
  employer: IEmployer;
  isCurrentUser: boolean;
}


const EmployerUserProfile: React.FC<EmployerUserProfileProps> = ({ employer: _employer, isCurrentUser }) => {

  const [showEditBasicInfoLightbox, setShowEditBasicInfoLightbox] = useState(false);
  const [showEditSocialLinksLightbox, setShowEditSocialLinksLightbox] = useState(false);
  const [showEditCompanyInfoLightbox, setShowEditCompanyInfoLightbox] = useState(false);


  const {
    error,
    currentUser,
    updateProfilePicture,
  } = useCurrentUser();
  const [employer, setEmployer] = useState<IEmployer>(_employer)

  const { sendVerificationEmail } = useAuth();
  const { addNotification } = useDisplayNotifications();

  const { openFilePicker, filesContent, plainFiles, loading } = useFilePicker({
    accept: ['image/*'], // Accept only image files
    multiple: false, // Allow only one file
  });

  const resendEmailVerification = () => {
    if (employer.email) {
      sendVerificationEmail().then(() => {
        addNotification(
          {
            message: 'Verification email sent',
            type: DisplayNotificationType.SUCCESS
          },
          true
        )
      }).catch(() => {
        addNotification(
          {
            message: 'An error occurred while sending the verification email',
            type: DisplayNotificationType.ERROR
          },
          true
        )
      });
    }
  }

  useEffect(() => {
    if (isCurrentUser) {
      setEmployer(currentUser as IEmployer);
    }
  }, [currentUser]);

  useEffect(() => {
    // Handle the selected file after the file picker is closed
    if (filesContent.length > 0) {
      // const file = filesContent[0];
      const plain = plainFiles[0];

      updateProfilePicture(plain).then((user) => {
      }).catch((e) => {
        addNotification(
          {
            message: error || 'An error occurred while updating the profile picture',
            type: DisplayNotificationType.ERROR
          },
          true
        )
      });
    }
  }, [filesContent]);


  return (
    <div className='p-4 space-y-4 max-w-7xl mx-auto'>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        {/* Left (mobile upper) columns */}
        <div>
          {/* Company Information */}
          <Card>
            <div className='my-4 flex flex-col'>

              {/* Profile Avatar section */}
              <div className='flex flex-col items-center'>
                <Avatar
                  imageUrl={
                    employer?.profilePictureUrl ||
                    DEFAULT_USER_PROFILE.DEFAULT_EMPLOYER_PROFILE_PICTURE
                  }
                  onUpload={isCurrentUser ? openFilePicker : undefined}
                />
                <div className='mt-4 flex flex-row space-x-4 items-center'>

                  <p className='body1 font-bold'>{getEmployerName(employer)}</p>
                  {employer.employerType && <div><Chip>{UserEnumTextMapper.employerType(employer.employerType)}</Chip></div>}
                </div>
                {/* Languages */}
                {(employer.languages?.length || 0) > 0 && <p className='body3'>{employer.languages?.map(language => language).join(', ')}</p>}
                {/* Email if current user - roles if not current user */}
                {isCurrentUser && <p className='body3 text-primary-700'>{employer.email}</p>}
                {/* Country if is other user */}
                {!isCurrentUser && employer.country && <p className='body3 text-primary-700'>{employer.country}</p>}


                {/* Resend verification mail */}
                {isCurrentUser && !employer._emailVerified
                  && <div className='mt-4'>
                    <FlatButton onClick={resendEmailVerification}>Resend Verification Email</FlatButton>
                  </div>}
              </div>

              {/* Basic Information */}
              {isCurrentUser && <div className='mt-6'>
                <BasicInformation
                  employer={employer}
                  isCurrentUser={isCurrentUser}
                  setShowEditCompanyInfoLightbox={setShowEditBasicInfoLightbox} />

              </div>}

              {/* Separator */}
              <hr className='m-4 border-neutral-300' />

              {/* Social Links */}
              <div>
                <SocialLinks employer={employer}
                  isCurrentUser={isCurrentUser}
                  setShowEditSocialLinksLightbox={setShowEditSocialLinksLightbox} />
              </div>
            </div>
          </Card>

        </div>

        {/* Right (mobile lower) columns */}
        <div className='flex flex-col space-y-4'>
          {isCurrentUser && <Card><WalletInfo /></Card>}
          <Card>
            <CompanyInformation
              employer={employer}
              isCurrentUser={isCurrentUser}
              setShowEditCompanyInfoLightbox={setShowEditCompanyInfoLightbox} />
          </Card>
        </div>
      </div >

      {showEditBasicInfoLightbox && <EditBasicCompanyInfoLightbox onClose={() => setShowEditBasicInfoLightbox(false)} />}
      {showEditCompanyInfoLightbox && <EditCompanyInfoLightbox onClose={() => setShowEditCompanyInfoLightbox(false)} />}
      {showEditSocialLinksLightbox && <EditSocialLinksEmployer onClose={() => setShowEditSocialLinksLightbox(false)} />}
    </div>
  );
};


const BasicInformation: React.FC<{ employer: IEmployer, isCurrentUser: boolean, setShowEditCompanyInfoLightbox: (show: boolean) => void }> = ({ employer, isCurrentUser, setShowEditCompanyInfoLightbox }) => {
  return (
    <div className='flex flex-col space-y-1'>
      <div className='flex flex-row justify-between items-center'>
        <p className='body2 text-primary-700'>Basic Information</p>
        {isCurrentUser && <OutlinedButton
          icon={<FiEdit3 />}
          iconPosition='leading'
          size='small'
          onClick={
            () => {
              setShowEditCompanyInfoLightbox(true);
            }}>
          Edit</OutlinedButton>}
      </div>


      {/* Company Name */}
      <ProfileLabelValue label='Company Name' value={getEmployerName(employer)} />

      <div className='flex flex-row space-x-4'>
        <ProfileLabelValue label='Email' value={employer.email || ''} />
        {employer._emailVerified
          ? <div className='mt-auto flex flex-row space-x-2 text-green-500 items-center'>
            <p>Verified</p>
            <IoIosCheckmarkCircleOutline />
          </div>
          : <div className='mt-auto flex flex-row space-x-2 text-red-500 items-center'>
            <p>Not yet Verified</p>
            <IoMdCloseCircleOutline />
          </div>}
      </div>
      <ProfileLabelValue label='Phone' value={employer.phone || 'Not set'} />
      {/* <ProfileLabelValue label='Date of Birth' value={freelancer.birthDate?.toLocaleDateString() || 'Not set'} /> */}
      <ProfileLabelValue label='Address' value={fullAddressString(
        employer.streetAddress,
        employer.streetAddress2,
        employer.city,
        employer.state,
        employer.zipCode,
        employer.country)
        || 'Not set'} />
    </div>
  );
}


const WalletInfo: React.FC = () => {

  const { wallet } = useCurrentUser();

  return (
    <div className='flex flex-col space-y-6 py-4'>
      <h4 className='text-primary-700'>Wallet</h4>

      <div className='flex flex-col'>
        <div className='flex flex-row space-x-2 items-center'>
          <h2 className='font-bold'>{((wallet as EmployerWallet).totalCredits || 0) - ((wallet as EmployerWallet).totalOccupiedCredits || 0)}</h2>
          <p className='body2 text-neutral-900'>Credits</p>
        </div>

      </div>

      <OutlinedButton fullWidth onClick={() => { console.log('View Transactions') }}>View Transactions</OutlinedButton>

    </div>
  );
}


const SocialLinks: React.FC<{ employer: IEmployer, isCurrentUser: boolean, setShowEditSocialLinksLightbox: (show: boolean) => void }> = ({ employer, isCurrentUser, setShowEditSocialLinksLightbox }) => {
  return (
    <div className='flex flex-col space-y-1'>
      <div className='flex flex-row justify-between items-center'>
        <p className='body2 text-primary-700'>Social Links</p>
        {isCurrentUser && <OutlinedButton
          icon={<FiEdit3 />}
          iconPosition='leading'
          size='small'
          onClick={
            () => {
              setShowEditSocialLinksLightbox(true);
            }}>
          Edit</OutlinedButton>}
      </div>

      {<ProfileLabelValue label='Linked In' value={employer.socialLinks?.linkedin
        ? <a href={employer.socialLinks?.linkedin} target="_blank" rel="noopener noreferrer">
          {employer.socialLinks?.linkedin}
        </a>
        : 'Not set'} />}

      {(employer.socialLinks?.facebook.length || 0) > 0 && <ProfileLabelValue
        label='Facebook'
        value={<a href={employer.socialLinks?.facebook} target="_blank" rel="noopener noreferrer">
          {employer.socialLinks?.facebook}
        </a>} />}

      {(employer.socialLinks?.x.length || 0) > 0 && <ProfileLabelValue
        label='X'
        value={<a href={employer.socialLinks?.x} target="_blank" rel="noopener noreferrer">
          {employer.socialLinks?.x}
        </a>} />}

      {(employer.socialLinks?.instagram.length || 0) > 0 && <ProfileLabelValue
        label='Instagram'
        value={<a href={employer.socialLinks?.instagram} target="_blank" rel="noopener noreferrer">
          {employer.socialLinks?.instagram}
        </a>} />}

      {(employer.socialLinks?.github.length || 0) > 0 && <ProfileLabelValue
        label='Github'
        value={<a href={employer.socialLinks?.github} target="_blank" rel="noopener noreferrer">
          {employer.socialLinks?.github}
        </a>} />}

      {(employer.socialLinks?.youtube.length || 0) > 0 && <ProfileLabelValue
        label='Youtube'
        value={<a href={employer.socialLinks?.youtube} target="_blank" rel="noopener noreferrer">
          {employer.socialLinks?.youtube}
        </a>} />}

      {(employer.socialLinks?.stackoverflow.length || 0) > 0 && <ProfileLabelValue
        label='Stackoverflow'
        value={<a href={employer.socialLinks?.stackoverflow} target="_blank" rel="noopener noreferrer">
          {employer.socialLinks?.stackoverflow}
        </a>} />}

      {(employer.socialLinks?.pinterest.length || 0) > 0 && <ProfileLabelValue
        label='Pinterest'
        value={<a href={employer.socialLinks?.pinterest} target="_blank" rel="noopener noreferrer">
          {employer.socialLinks?.pinterest}
        </a>} />}

      {(employer.socialLinks?.tiktok.length || 0) > 0 && <ProfileLabelValue
        label='Tiktok'
        value={<a href={employer.socialLinks?.tiktok} target="_blank" rel="noopener noreferrer">
          {employer.socialLinks?.tiktok}
        </a>} />}

      {(employer.socialLinks?.reddit.length || 0) > 0 && <ProfileLabelValue
        label='Reddit'
        value={<a href={employer.socialLinks?.reddit} target="_blank" rel="noopener noreferrer">
          {employer.socialLinks?.reddit}
        </a>} />}

      {(employer.socialLinks?.behance.length || 0) > 0 && <ProfileLabelValue
        label='Behance'
        value={<a href={employer.socialLinks?.behance} target="_blank" rel="noopener noreferrer">
          {employer.socialLinks?.behance}
        </a>} />}

      {(employer.socialLinks?.dribbble.length || 0) > 0 && <ProfileLabelValue
        label='Dribbble'
        value={<a href={employer.socialLinks?.dribbble} target="_blank" rel="noopener noreferrer">
          {employer.socialLinks?.dribbble}
        </a>} />}

      {(employer.socialLinks?.websites.length || 0) > 0 && <ProfileLabelValue
        label='Other'
        value={<div className='flex flex-col'>
          {employer.socialLinks?.websites.map((url, index) => (
            <a key={index} href={url} target="_blank" rel="noopener noreferrer">
              {url}
            </a>
          ))}
        </div>} />}
    </div>
  );
}


const CompanyInformation: React.FC<{ employer: IEmployer, isCurrentUser: boolean, setShowEditCompanyInfoLightbox: (show: boolean) => void }> = ({ employer, isCurrentUser, setShowEditCompanyInfoLightbox }) => {
  return (
    <div className='flex flex-col space-y-4'>

      {/* Bio */}
      <div className='flex flex-row justify-between items-center'>
        <p className='body2 primary-900 font-bold'>Bio</p>
        {isCurrentUser && <OutlinedButton
          icon={<FiEdit3 />}
          iconPosition='leading'
          size='small'
          onClick={
            () => {
              setShowEditCompanyInfoLightbox(true);
            }}>
          Edit</OutlinedButton>}
      </div>
      <RichText text={employer.bio || 'Not set'} />

      {/* Industries */}
      {(employer.industries?.length || 0) > 0 && <div className='mt-6'>
        <p className='body2 primary-900 font-bold'>Industries</p>
        <div className='flex flex-wrap gap-2'>
          {employer.industries!.map((industry) => (<div key={industry} className=''>
            <Chip>
              {industry}
            </Chip>
          </div>))}
        </div>
      </div>}

      {/* Business Types */}
      {(employer.industries?.length || 0) > 0 && <div className='mt-6'>
        <p className='body2 primary-900 font-bold'>Business Type</p>
        <div className='flex flex-wrap gap-2'>
          {employer.typesOfBusiness!.map((type) => (<div key={type} className=''>
            <Chip>
              {UserEnumTextMapper.typeOfBusiness(type)}
            </Chip>
          </div>))}
        </div>
      </div>}

      {/* Private Info */}
      {isCurrentUser && <div className='border border-neutral-300 p-4 flex flex-col space-y-4 rounded-xl'>
        <p className='caption2'>Private Information - This information helps us to provide better solutions. We use it amongst others for better matchmaking. It won't be shared with anyone and is only visible to you.</p>
        {/* Number Employees */}
        <div className='flex flex-row items-center space-x-4'>
          <p className='primary-900'>Number of Employees</p>
          <p className="font-semibold text-primary-700">{(employer.numberEmployees?.length || 0) > 0 ? employer.numberEmployees : 'Not set'}</p>
        </div>

        {/* Number Employees */}
        <div className='flex flex-row items-center space-x-4'>
          <p className='primary-900'>Revenue Range</p>
          <p className="font-semibold text-primary-700">{(employer.revenueRange?.length || 0) > 0 ? employer.revenueRange : 'Not set'}</p>
        </div>

        {/* Number Employees */}
        <div className='flex flex-row items-center space-x-4'>
          <p className='primary-900'>Startup Stage (if you are one)</p>
          <p className="font-semibold text-primary-700">{employer.startupStage ? UserEnumTextMapper.startupStage(employer.startupStage) : 'Not set'}</p>
        </div>
      </div>}
    </div>
  );
}

export default EmployerUserProfile;
